import React from 'react';
import { useTranslation } from 'react-i18next';

import { Header } from './ui/header/Header';
import { Router } from './Router';
import { Footer } from './ui/footer/Footer';
import { CookieConsentBar } from './ui/cookie-consent-bar/CookieConsentBar';
import { useDidMount } from '@shared/hooks/useDidMount';
import { mapUserResponseToUser } from '@shared/lib/mapUserResponse';
import { useAuth } from './AuthHook';
import { ToastContainer } from 'react-toastify';
import { Loader, useTheme } from '@igo/ui';
import { useFullscreen } from '@shared/hooks/useFullscreen';
import { MaintenancePage } from './ui/maintenance-page/MaintenancePage';
import YandexMetrika from './YandexMetrika';
import { userServiceGetUserSelf } from '@/api';
import { consumeAuthReturnTo } from '@shared/lib/authReturnTo';
import { checkSessionWithRetry, shouldRecheckSession } from './checkSession';
import { useLocation } from 'react-router-dom';

export const App: React.FC = () => {
    const isMaintenanceMode = process.env.MAINTENANCE_MODE === 'true';

    const [, i18n] = useTranslation();
    const { setUser, isUserDidFetch, setUserDidFetch } = useAuth();
    const { theme } = useTheme();

    const { isFullscreen } = useFullscreen();
    const { pathname } = useLocation();
    const isClubVoucherPage = pathname === '/promo';
    const activeTheme = isClubVoucherPage ? 'light' : theme;

    useDidMount(() => {
        const updateHtmlLang = (lng: string) => {
            document.documentElement.lang = lng;
            document.querySelector('meta[http-equiv="Content-Language"]')?.setAttribute('content', lng);
            document.title = i18n.t('title', { ns: 'global' });
        };
        updateHtmlLang(i18n.language);
        i18n.on('languageChanged', updateHtmlLang);

        let cancelled = false;
        let sessionCheckInFlight = false;
        let lastSessionCheckAt = Date.now();

        const recheckSession = async (initial: boolean) => {
            if (sessionCheckInFlight || !setUser || !setUserDidFetch) return;
            sessionCheckInFlight = true;

            try {
                const result = await checkSessionWithRetry(userServiceGetUserSelf);
                if (cancelled) return;

                if (result.status === 'authenticated') {
                    setUser(mapUserResponseToUser(result.data ?? {}));

                    if (initial) {
                        const returnTo = consumeAuthReturnTo();
                        if (returnTo) window.location.replace(returnTo);
                    }
                } else if (result.status === 'unauthenticated') {
                    setUser(null);
                }

                if (initial) setUserDidFetch(true);
            } finally {
                lastSessionCheckAt = Date.now();
                sessionCheckInFlight = false;
            }
        };

        const handleReturnToTab = () => {
            if (shouldRecheckSession(lastSessionCheckAt)) void recheckSession(false);
        };

        if (!isMaintenanceMode) {
            if (!isUserDidFetch) void recheckSession(true);
            document.addEventListener('visibilitychange', handleReturnToTab);
            window.addEventListener('focus', handleReturnToTab);
        }

        return () => {
            cancelled = true;
            i18n.off('languageChanged', updateHtmlLang);
            document.removeEventListener('visibilitychange', handleReturnToTab);
            window.removeEventListener('focus', handleReturnToTab);
        };
    });

    if (isMaintenanceMode) return <MaintenancePage />;

    if (!isUserDidFetch) {
        return (
            <div className="app app--auth-loading">
                <Loader />
            </div>
        );
    }

    return (
        <div className="app" data-theme={isClubVoucherPage ? 'light' : undefined}>
            <YandexMetrika />
            {!isFullscreen && !isClubVoucherPage && <Header />}
            <Router key={i18n.language} />
            <CookieConsentBar />
            <ToastContainer style={{ top: '85px' }} theme={activeTheme} hideProgressBar pauseOnFocusLoss={false} />
            {!isFullscreen && <Footer />}
        </div>
    );
};
